import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, Plus, ChevronDown, ChevronRight,
  Users, Pencil, Trash2, X, Check, RefreshCw,
  CalendarDays, Clock, Building2,
} from 'lucide-react';
import { useDepartments } from '../hooks/useDepartments';
import { useTeamMembers } from '../hooks/useTeamMembers';
import { useApp } from '../context/AppContext';
import apiService from '../services/api';
import { EmployeeCard, MembersList } from '../components/team/EmployeeCard';
import { EmployeeProfile } from '../components/team/EmployeeDrawer';

const PRIMARY = 'rgb(28, 26, 20)';

const DEPT_COLORS = [
  PRIMARY, PRIMARY, PRIMARY, PRIMARY,
  PRIMARY, PRIMARY, PRIMARY, PRIMARY,
];
function getColor(idx) { return DEPT_COLORS[idx % DEPT_COLORS.length]; }

function mapUser(user, deptName, color) {
  const firstName = user.firstName ?? '';
  const lastName  = user.lastName ?? '';
  const fullName  = (firstName || lastName)
    ? `${firstName} ${lastName}`.trim()
    : (user.name ?? user.email ?? '');
  return {
    ...user,
    name: fullName,
    firstName,
    lastName,
    dept: deptName,
    role: user.position ?? user.DepartmentMember?.role ?? 'Member',
    balance: user.leaveBalance ?? user.annualLeaveBalance ?? 25,
    used: user.leaveUsed ?? user.annualLeaveUsed ?? 0,
    pending: user.leavePending ?? user.pendingLeave ?? 0,
    color: user.color ?? color,
  };
}

// Shape an employee for the list and the profile page
function toListEmployee(e) {
  const primaryDept = e.departments?.[0];
  const firstName   = e.firstName ?? '';
  const lastName    = e.lastName  ?? '';
  return {
    ...e,
    name: (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (e.name ?? e.email ?? ''),
    dept:         primaryDept?.departmentName ?? e.dept ?? null,
    departmentId: primaryDept?.departmentId   ?? e.departmentId ?? null, // profile uses this to pre-select the dropdown
    balance: e.leaveBalance ?? e.annualLeaveBalance ?? e.balance ?? 25,
    used:    e.leaveUsed   ?? e.annualLeaveUsed    ?? e.used    ?? 0,
    pending: e.leavePending ?? e.pendingLeave       ?? e.pending ?? 0,
  };
}

// The open profile lives in the URL (?person=<id>) so it can be linked to
// and the browser's back button returns to the list
function personFromUrl() {
  try { return new URLSearchParams(window.location.search).get('person'); } catch { return null; }
}
function setPersonInUrl(id, replace = false) {
  try {
    const url = new URL(window.location.href);
    if (id == null) url.searchParams.delete('person'); else url.searchParams.set('person', id);
    const fn = replace ? 'replaceState' : 'pushState';
    window.history[fn]({ ...(window.history.state || {}), person: id ?? null }, '', url.toString());
  } catch { /* history unavailable */ }
}

function matchesQuery(user, q) {
  if (!q) return true;
  const full = `${user.firstName ?? ''} ${user.lastName ?? ''} ${user.name ?? ''}`.toLowerCase();
  return full.includes(q) || (user.email ?? '').toLowerCase().includes(q);
}

// ── Shared modal shell ────────────────────────────────────────
function Modal({ title, subtitle, onClose, children, footer, width = 420 }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="tm-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="tm-modal" role="dialog" aria-modal="true" aria-label={title} style={{ width: `min(${width}px, calc(100vw - 32px))` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{title}</h2>
            {subtitle && <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '3px 0 0' }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} className="tm-icon-btn" aria-label="Close"><X size={16} /></button>
        </div>
        {children}
        {footer && <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </div>
  );
}

// ── Add Department Modal ──────────────────────────────────────
function AddDeptModal({ onClose, onAdd, loading }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) { setError('Department name is required.'); return; }
    try { await onAdd(trimmed, desc.trim()); onClose(); }
    catch { /* handled in hook */ }
  }

  return (
    <Modal
      title="Add department"
      subtitle="Group people so you can filter the calendar and spot clashes."
      onClose={onClose}
      footer={<>
        <button className="tm-btn" onClick={onClose}>Cancel</button>
        <button className="tm-btn tm-btn-primary" onClick={handleSubmit} disabled={loading} style={{ opacity: loading ? 0.7 : 1 }}>
          <Plus size={15} /> {loading ? 'Creating…' : 'Add department'}
        </button>
      </>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label className="tm-field">
          <span className="tm-label">Name</span>
          <input autoFocus className="tm-input" placeholder="e.g. Product, Legal, Finance…" value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            aria-invalid={!!error}
            style={error ? { borderColor: 'var(--bad-border)' } : undefined}
          />
          {error && <span style={{ fontSize: 12, color: 'var(--bad-text)' }}>{error}</span>}
        </label>
        <label className="tm-field">
          <span className="tm-label">Description <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}>(optional)</span></span>
          <input className="tm-input" placeholder="What does this department do?" value={desc}
            onChange={e => setDesc(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          />
        </label>
      </div>
    </Modal>
  );
}

// ── Add Member Modal (for department grouped view) ────────────
function AddMemberModal({ dept, allEmployees, departmentMemberIds, onClose, onAdd }) {
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(null);

  const available = allEmployees.filter(e => {
    if (departmentMemberIds.has(e.id)) return false;
    const full = `${e.firstName ?? ''} ${e.lastName ?? ''} ${e.name ?? ''}`.toLowerCase();
    return full.includes(search.toLowerCase()) ||
      (e.email ?? '').toLowerCase().includes(search.toLowerCase());
  });

  async function handleAdd(emp) {
    setAdding(emp.id);
    try { await onAdd(emp.id); onClose(); }
    catch { setAdding(null); }
  }

  function initials(emp) {
    return ((emp.firstName?.[0] ?? '') + (emp.lastName?.[0] ?? '')).toUpperCase()
      || (emp.email?.[0] ?? '?').toUpperCase();
  }

  return (
    <Modal title="Add member" subtitle={`to ${dept.name}`} onClose={onClose} width={460}>
      <div className="tm-search">
        <Search size={15} />
        <input autoFocus className="tm-input" placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '50vh', margin: '0 -8px', padding: '0 8px' }}>
        {available.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            {search ? `No one matches “${search}”` : 'Everyone is already in this department'}
          </div>
        ) : available.map(emp => (
          <button key={emp.id} onClick={() => handleAdd(emp)} disabled={adding === emp.id} className="tm-pick" style={{ opacity: adding === emp.id ? 0.6 : 1 }}>
            {emp.profileImageUrl ? (
              <img src={emp.profileImageUrl} alt={initials(emp)} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: emp.color ?? PRIMARY, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff' }}>
                {initials(emp)}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.firstName} {emp.lastName}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '1px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{emp.position ? `${emp.position} · ` : ''}{emp.email}</p>
            </div>
            <span className="tm-pick-add">
              {adding === emp.id ? <RefreshCw size={13} style={{ animation: 'tmSpin 1s linear infinite' }} /> : <Plus size={14} />}
              {adding === emp.id ? 'Adding' : 'Add'}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}

// ── Department Section ────────────────────────────────────────
function DeptSection({ dept, color, allEmployees, query, onSelectEmp, onAddMember, onRemoveMember, onRename, onDelete }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(dept.name);
  const [saving, setSaving] = useState(false);

  const members = dept.members ?? [];
  const memberIdSet = useMemo(() => new Set(members.map(m => m.id)), [members]);
  const visibleMembers = useMemo(() => members.filter(m => matchesQuery(m, query)), [members, query]);

  async function commitRename() {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === dept.name) { setRenaming(false); return; }
    setSaving(true);
    try { await onRename(dept.id, trimmed); }
    finally { setSaving(false); setRenaming(false); }
  }

  function handleDelete() {
    if (window.confirm(`Delete the ${dept.name} department?`)) onDelete(dept.id);
  }

  // While searching, hide departments with nobody matching
  if (query && visibleMembers.length === 0) return null;
  const isOpen = !collapsed || !!query;

  return (
    <>
      <section className="tm-panel">
        {/* Header */}
        <div className="tm-dept-head" style={{ borderBottom: isOpen ? '1px solid var(--border-soft)' : 'none' }}>
          <button
            onClick={() => setCollapsed(c => !c)}
            className="tm-icon-btn"
            aria-expanded={isOpen}
            aria-label={isOpen ? `Collapse ${dept.name}` : `Expand ${dept.name}`}
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <span style={{ width: 32, height: 32, borderRadius: 9, background: 'var(--tint)', color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Building2 size={15} />
          </span>
          {renaming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
              <input autoFocus value={draftName} onChange={e => setDraftName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(false); }}
                className="tm-input" style={{ height: 34, maxWidth: 280, fontWeight: 600 }}
                aria-label="Department name"
              />
              <button onClick={commitRename} disabled={saving} className="tm-icon-btn" style={{ color: 'var(--ok-text)' }} aria-label="Save name"><Check size={16} /></button>
              <button onClick={() => setRenaming(false)} className="tm-icon-btn" aria-label="Cancel rename"><X size={16} /></button>
            </div>
          ) : (
            <button type="button" onClick={() => setCollapsed(c => !c)} style={{ flex: 1, minWidth: 0, border: 'none', background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dept.name}</span>
                <span className="tm-pill">{members.length}</span>
              </span>
              {dept.description && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dept.description}</span>}
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button onClick={() => setShowAdd(true)} className="tm-btn tm-btn-sm">
              <Plus size={14} /> <span className="tm-hide-sm">Add member</span>
            </button>
            <button onClick={() => { setDraftName(dept.name); setRenaming(true); }} className="tm-icon-btn" title="Rename" aria-label={`Rename ${dept.name}`}>
              <Pencil size={14} />
            </button>
            <button onClick={handleDelete} className="tm-icon-btn tm-icon-btn-danger" title="Delete" aria-label={`Delete ${dept.name}`}>
              <Trash2 size={14} />
            </button>
          </div>
        </div>

        {/* Member grid */}
        {isOpen && (
          <div style={{ padding: 18 }}>
            {members.length === 0 ? (
              <button type="button" onClick={() => setShowAdd(true)} className="tm-empty">
                <Users size={20} />
                <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>No members yet</span>
                <span>Add someone to get started</span>
              </button>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
                {visibleMembers.map(user => (
                  <div key={user.id} className="tm-member">
                    <EmployeeCard employee={mapUser(user, dept.name, color)} onSelect={onSelectEmp} hideDept />
                    <button
                      onClick={() => onRemoveMember(dept.id, user.id)}
                      title="Remove from department"
                      aria-label={`Remove ${user.firstName ?? ''} ${user.lastName ?? ''} from ${dept.name}`}
                      className="tm-remove"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {showAdd && (
        <AddMemberModal
          dept={dept}
          allEmployees={allEmployees}
          departmentMemberIds={memberIdSet}
          onClose={() => setShowAdd(false)}
          onAdd={(userId) => onAddMember(dept.id, userId)}
        />
      )}
    </>
  );
}

// ── Loading placeholder ───────────────────────────────────────
function SkeletonList() {
  return (
    <div className="tm-panel" aria-busy="true" aria-label="Loading team">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < 5 ? '1px solid var(--border-soft)' : 'none' }}>
          <span className="tm-skel" style={{ width: 36, height: 36, borderRadius: '50%' }} />
          <span className="tm-skel" style={{ width: 140 + (i % 3) * 30, height: 12 }} />
          <span className="tm-skel tm-hide-sm" style={{ width: 180, height: 12, marginLeft: 'auto' }} />
          <span className="tm-skel tm-hide-sm" style={{ width: 80, height: 20, borderRadius: 10 }} />
        </div>
      ))}
    </div>
  );
}

// ── Main Team Page ────────────────────────────────────────────
export default function Team() {
  const { currentUser, company, summary, isAdmin, isApprover, approvalScope } = useApp();

  // Everyone's balance from the leave engine (the same figure as the profile,
  // calendar and dashboard). Admins: everyone; managers: their team; others: themselves.
  const [balances, setBalances] = useState({});
  useEffect(() => {
    if (!currentUser) return;
    apiService.getLeaveBalances({ year: new Date().getFullYear() })
      .then(res => setBalances(res?.data && typeof res.data === 'object' ? res.data : {}))
      .catch(() => setBalances({}));
  }, [currentUser, summary?.pendingForMe]); // eslint-disable-line react-hooks/exhaustive-deps
  const canSeeBalanceOf = (id) => isAdmin || id === currentUser?.id || (approvalScope?.userIds ?? []).includes(id);

  const { employees, loading: membersLoading, refetch: refetchMembers } = useTeamMembers();

  const {
    departments,
    loading: deptsLoading,
    createDepartment,
    renameDepartment,
    deleteDepartment,
    addMember,
    removeMember,
  } = useDepartments();

  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [showAddDept, setShowAddDept] = useState(false);
  const [view, setView] = useState('all');
  const [creating, setCreating] = useState(false);

  const loading = membersLoading || deptsLoading;
  const query = search.trim().toLowerCase();

  const deptColorMap = useMemo(() => {
    const map = {};
    departments.forEach((d, i) => { map[d.id] = getColor(i); });
    return map;
  }, [departments]);

  const filteredEmps = useMemo(() =>
    employees
      .filter(e => {
        const full = `${e.firstName ?? ''} ${e.lastName ?? ''} ${e.name ?? ''}`.toLowerCase();
        return full.includes(search.toLowerCase()) ||
          (e.email ?? '').toLowerCase().includes(search.toLowerCase());
      })
      .map(toListEmployee),
    [employees, search]
  );

  // ─── Profile page open/close, kept in step with the URL ───
  function openProfile(emp) {
    setSelected(emp);
    setPersonInUrl(emp.id);
  }
  function closeProfile() {
    setSelected(null);
    // Go back if we pushed the entry ourselves; otherwise just tidy the URL
    if (window.history.state?.person != null) window.history.back();
    else setPersonInUrl(null, true);
    refetchMembers?.(); // pick up any edits made on the profile
  }
  // Back / forward buttons
  useEffect(() => {
    const onPop = () => {
      const id = personFromUrl();
      if (!id) { setSelected(null); return; }
      const emp = employees.find(e => String(e.id) === String(id));
      if (emp) setSelected(toListEmployee(emp));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [employees]);
  // Opening a shared link straight to someone's profile
  useEffect(() => {
    const id = personFromUrl();
    if (!id || selected || !employees.length) return;
    const emp = employees.find(e => String(e.id) === String(id));
    if (emp) setSelected(toListEmployee(emp));
    else setPersonInUrl(null, true);
  }, [employees]); // eslint-disable-line react-hooks/exhaustive-deps

  // Average days left, over the people whose balance is tracked and visible
  const tracked = employees
    .map(e => balances[e.id]?.balance)
    .filter(b => b && !b.noBalanceTracking && b.available);
  const avgRemaining = tracked.length
    ? Math.round((tracked.reduce((s, b) => s + Number(b.available.days || 0), 0) / tracked.length) * 2) / 2
    : null;
  // Requests waiting for a decision (a count of requests, never days)
  const totalPending = isApprover ? (summary?.pendingForMe ?? 0) : (summary?.myPending ?? 0);

  const groupedHasMatches = !query || departments.some(d => (d.members ?? []).some(m => matchesQuery(m, query)));

  async function handleCreateDept(name, description) {
    setCreating(true);
    try { await createDepartment(name, description); }
    finally { setCreating(false); }
  }

  // Called when AddUserModal successfully creates a user
  function handleUserCreated(newUser) {
    refetchMembers?.();
  }

  // Derive display name for current user
  const currentUserName = currentUser
    ? `${currentUser.firstName ?? ''} ${currentUser.lastName?.[0] ?? ''}`.trim() || currentUser.email
    : 'Admin';
  const companyName = company?.name ?? 'Your Company';

  if (selected) {
    return <EmployeeProfile key={selected.id} employee={selected} onBack={closeProfile} />;
  }

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes tmSpin { to { transform: rotate(360deg); } }
        @keyframes tmShimmer { 0% { background-position: -200px 0; } 100% { background-position: 200px 0; } }
        @keyframes tmPop { from { opacity: 0; transform: translateY(6px) scale(0.98); } to { opacity: 1; transform: none; } }

        .tm-btn {
          height: 40px; padding: 0 16px; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
          border: 1px solid var(--border); border-radius: 10px; background: var(--surface); color: var(--text-2);
          font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer; flex-shrink: 0;
          transition: background 0.15s, border-color 0.15s;
        }
        .tm-btn:hover { background: var(--border-soft); border-color: var(--field-border); }
        .tm-btn:focus-visible, .tm-icon-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
        .tm-btn-primary { background: #FFC81E; border-color: #FFC81E; color: #1C1A14; }
        .tm-btn-primary:hover { background: #F5A300; border-color: #F5A300; }
        .tm-btn-sm { height: 32px; padding: 0 12px; font-size: 12.5px; border-radius: 9px; gap: 6px; }

        .tm-icon-btn {
          width: 32px; height: 32px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
          border: 1px solid transparent; border-radius: 9px; background: transparent; color: var(--text-muted); cursor: pointer;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .tm-icon-btn:hover { background: var(--surface); border-color: var(--border); color: var(--text); }
        .tm-icon-btn-danger:hover { background: var(--bad-bg); border-color: var(--bad-border); color: var(--bad-text); }

        .tm-seg { display: flex; gap: 2px; padding: 3px; background: var(--border-soft); border-radius: 10px; }
        .tm-seg button {
          height: 32px; padding: 0 14px; border: none; border-radius: 8px; background: transparent;
          color: var(--text-2); font-size: 13px; font-weight: 500; white-space: nowrap; cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .tm-seg button:hover { color: var(--text); }
        .tm-seg button[aria-pressed="true"] { background: var(--surface); color: var(--accent); font-weight: 600; box-shadow: 0 1px 2px rgba(28,26,20,0.10); }

        .tm-input {
          width: 100%; height: 40px; padding: 0 12px; box-sizing: border-box;
          border: 1px solid var(--border); border-radius: 10px; background: var(--surface-2);
          color: var(--text); font-size: 13px; outline: none; transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
        }
        .tm-input::placeholder { color: var(--text-muted); }
        .tm-input:focus { background: var(--surface); border-color: var(--accent); box-shadow: 0 0 0 3px var(--tint); }
        .tm-search { position: relative; }
        .tm-search > svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
        .tm-search > .tm-input { padding-left: 36px; }

        .tm-field { display: flex; flex-direction: column; gap: 6px; }
        .tm-label { font-size: 12.5px; font-weight: 600; color: var(--text-2); }

        .tm-strip { display: flex; flex-wrap: wrap; gap: 8px; }
        .tm-chip { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px; border: 1px solid var(--border); border-radius: 16px; background: var(--surface); color: var(--text-2); font-size: 12.5px; white-space: nowrap; }
        .tm-chip svg { color: var(--text-muted); flex-shrink: 0; }
        .tm-chip b { color: var(--text); font-weight: 700; }
        .tm-chip-warn { background: var(--warn-bg-soft); border-color: var(--warn-border); color: var(--warn-text); }
        .tm-chip-warn b, .tm-chip-warn svg { color: var(--warn-text); }

        .tm-panel { background: var(--surface); border: 1px solid var(--border); border-radius: 18px; overflow: hidden; box-shadow: 0 1px 2px rgba(28,26,20,0.03); }
        .tm-dept-head { display: flex; align-items: center; gap: 10px; padding: 12px 16px 12px 12px; background: var(--surface-2); }
        .tm-pill { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: 10px; background: var(--border); color: var(--text-2); font-size: 11.5px; font-weight: 600; flex-shrink: 0; }

        .tm-empty {
          width: 100%; display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 28px 0;
          border: 1.5px dashed var(--border); border-radius: 14px; background: transparent; color: var(--text-muted); font-size: 13px;
          cursor: pointer; transition: border-color 0.15s, background 0.15s;
        }
        .tm-empty:hover { border-color: var(--field-border); background: var(--surface-2); }
        .tm-empty svg { opacity: 0.5; margin-bottom: 4px; }

        .tm-member { position: relative; }
        .tm-remove {
          position: absolute; top: -7px; right: -7px; width: 22px; height: 22px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center; z-index: 5; cursor: pointer;
          border: 1px solid var(--bad-border); background: var(--surface); color: var(--bad-text); box-shadow: 0 1px 3px rgba(28,26,20,0.12);
          opacity: 0; transform: scale(0.85); transition: opacity 0.15s, transform 0.15s;
        }
        .tm-member:hover .tm-remove, .tm-remove:focus-visible { opacity: 1; transform: scale(1); }
        .tm-remove:hover { background: var(--bad-bg); }
        @media (hover: none) { .tm-remove { opacity: 1; transform: none; } }

        .tm-overlay { position: fixed; inset: 0; z-index: 50; background: var(--overlay); backdrop-filter: blur(3px); display: flex; align-items: center; justify-content: center; padding: 16px; }
        .tm-modal { background: var(--surface); border-radius: 18px; padding: 24px; border: 1px solid var(--border); box-shadow: 0 24px 70px rgba(28,26,20,0.22); display: flex; flex-direction: column; gap: 18px; max-height: calc(100vh - 32px); box-sizing: border-box; animation: tmPop 0.2s cubic-bezier(0.22, 1, 0.36, 1) both; }

        .tm-pick { display: flex; align-items: center; gap: 12px; width: 100%; padding: 10px; border: none; border-radius: 12px; background: transparent; cursor: pointer; text-align: left; transition: background 0.12s; }
        .tm-pick:hover { background: var(--surface-2); }
        .tm-pick-add { display: inline-flex; align-items: center; gap: 4px; height: 28px; padding: 0 10px; border-radius: 8px; background: var(--tint); color: var(--accent); font-size: 12px; font-weight: 600; flex-shrink: 0; opacity: 0; transition: opacity 0.12s; }
        .tm-pick:hover .tm-pick-add, .tm-pick:focus-visible .tm-pick-add, .tm-pick:disabled .tm-pick-add { opacity: 1; }
        @media (hover: none) { .tm-pick-add { opacity: 1; } }

        .tm-skel { display: inline-block; background: linear-gradient(90deg, var(--border-soft) 0, var(--surface-2) 50%, var(--border-soft) 100%); background-size: 400px 100%; animation: tmShimmer 1.2s linear infinite; border-radius: 6px; flex-shrink: 0; }

        @media (max-width: 640px) { .tm-hide-sm { display: none !important; } }
      `}</style>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
          <div className="tm-search" style={{ width: 'min(280px, 100%)' }}>
            <Search size={15} />
            <input
              type="search"
              className="tm-input"
              style={{ background: 'var(--surface)' }}
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search team"
            />
          </div>
          <div className="tm-seg" role="group" aria-label="View">
            {[{ key: 'all', label: 'All members' }, { key: 'grouped', label: 'By department' }].map(v => (
              <button key={v.key} aria-pressed={view === v.key} onClick={() => setView(v.key)}>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        <button onClick={() => setShowAddDept(true)} className="tm-btn tm-btn-primary">
          <Plus size={15} /> Add department
        </button>
      </div>

      {/* Summary strip */}
      <div className="tm-strip">
        <span className="tm-chip"><Users size={14} /><b>{employees.length}</b> {employees.length === 1 ? 'member' : 'members'}</span>
        <span className="tm-chip"><Building2 size={14} /><b>{departments.length}</b> {departments.length === 1 ? 'department' : 'departments'}</span>
        {avgRemaining != null && (
          <span className="tm-chip" title="Average annual leave left this leave year, across people whose balance is tracked"><CalendarDays size={14} /><b>{avgRemaining}d</b> average left</span>
        )}
        <a href="/requests" className={'tm-chip' + (totalPending > 0 ? ' tm-chip-warn' : '')} style={{ textDecoration: 'none' }} title={isApprover ? 'Requests waiting for your approval' : 'Your requests waiting for approval'}>
          <Clock size={14} /><b>{totalPending}</b> {totalPending === 1 ? 'request' : 'requests'} {isApprover ? 'to approve' : 'waiting'}
        </a>
      </div>

      {/* Loading */}
      {loading && <SkeletonList />}

      {/* Grouped view */}
      {!loading && view === 'grouped' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {departments.length === 0 ? (
            <div className="tm-panel" style={{ padding: 20 }}>
              <button type="button" className="tm-empty" onClick={() => setShowAddDept(true)}>
                <Building2 size={20} />
                <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>No departments yet</span>
                <span>Create one to start grouping your team</span>
              </button>
            </div>
          ) : !groupedHasMatches ? (
            <div className="tm-panel" style={{ textAlign: 'center', padding: '48px 20px', fontSize: 14, color: 'var(--text-2)' }}>
              No one matches “{search.trim()}”.{' '}
              <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Clear search</button>
            </div>
          ) : departments.map(dept => (
            <DeptSection
              key={dept.id}
              dept={dept}
              color={deptColorMap[dept.id]}
              allEmployees={employees}
              query={query}
              onSelectEmp={openProfile}
              onAddMember={addMember}
              onRemoveMember={removeMember}
              onRename={renameDepartment}
              onDelete={deleteDepartment}
            />
          ))}
        </div>
      )}

      {/* All members list */}
      {!loading && view === 'all' && (
        filteredEmps.length === 0 && search ? (
          <div className="tm-panel" style={{ textAlign: 'center', padding: '56px 20px', fontSize: 14, color: 'var(--text-2)' }}>
            No one matches “{search.trim()}”.{' '}
            <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>Clear search</button>
          </div>
        ) : (
          <MembersList
            employees={filteredEmps}
            onSelect={openProfile}
            onUserCreated={handleUserCreated}
            departments={departments}
            currentUserName={currentUserName}
            companyName={companyName}
            balances={balances}
            currentUser={currentUser}
            canSeeBalanceOf={canSeeBalanceOf}
          />
        )
      )}

      {showAddDept && (
        <AddDeptModal
          loading={creating}
          onClose={() => setShowAddDept(false)}
          onAdd={handleCreateDept}
        />
      )}

    </div>
  );
}